import React, { useEffect, useState, useRef } from 'react';
import FixedElementsPortal from './FixedElementsPortal';
import PropTypes from 'prop-types';

// This component wraps other components that should appear in a sticky, bottom-fixed position on mobile devices.
// Prop `setForDesktop`: when `true`, the wrapped component will also behave as a sticky element on desktop,
// but instead of sticking to the bottom, it sticks to the top after being scrolled past.
// The current implementation supports basic sticky behavior for mobile.
// To use it on desktop freely, more enhancements (eg. setting up margins via props instead of hard-coded value) are needed.

const ResponsiveFixedPortalWrapper = ({ setForDesktop, children, valueToAdjustTop }) => {
  const [isSmallDevice, setIsSmallDevice] = useState(window.innerWidth <= 768);
  const [showStickyForDesktop, setShowStickyForDesktop] = useState(false);
  const [pointToStickOnDesktop, setPointToStickOnDesktop] = useState();
  const [originalElementHeight, setOriginalElementHeight] = useState();

  const containerRef = useRef(null);
  useEffect(() => {
    if (setForDesktop && containerRef.current) {
      // adjustement is related to: nav height + padding top
      setPointToStickOnDesktop(
        containerRef.current.getBoundingClientRect().top - 90 - valueToAdjustTop
      );
      setOriginalElementHeight(containerRef.current.clientHeight);
    }
  }, [containerRef]);

  useEffect(() => {
    if (setForDesktop) {
      const handleScroll = () => {
        if (window.pageYOffset > pointToStickOnDesktop) {
          setShowStickyForDesktop(true);
        } else if (setShowStickyForDesktop) {
          setShowStickyForDesktop(false);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  });

  useEffect(() => {
    const handleWindowSizeChange = () => {
      const newWidth = window.innerWidth;
      setIsSmallDevice(newWidth <= 768);
    };

    window.addEventListener('resize', handleWindowSizeChange);

    handleWindowSizeChange();

    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  // note: to avoid flashy effect on desktop, div of height of the element + its padding is added
  return isSmallDevice || showStickyForDesktop ? (
    <>
      {showStickyForDesktop && (
        <div style={{ height: `${originalElementHeight + 40}px`, width: '100%' }}></div>
      )}
      <FixedElementsPortal id="responsive-portal" isMobileLayout={isSmallDevice}>
        {children}
      </FixedElementsPortal>
    </>
  ) : (
    <div ref={containerRef} className="mb-40">
      {children}
    </div>
  );
};

ResponsiveFixedPortalWrapper.propTypes = {
  setForDesktop: PropTypes.bool,
  children: PropTypes.node,
  valueToAdjustTop: PropTypes.number,
};

export default ResponsiveFixedPortalWrapper;
